import { TextButton } from '@creditornot/cb-button';
import { colors, cbSemanticCssVars, typographyCss } from '@creditornot/cb-ingredients';
import { Spinner } from '@creditornot/cb-progress';
import { SegmentedControl } from '@creditornot/cb-segmented-control';
import React, { useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Typography as T } from 'common/typography';
import {
  Line,
  fetchGoogleSheetsData,
  makeCsvFile,
  selectGoogleSpreadsheet,
} from 'modules/menu/google-sheets/googleSheets';

type DocumentResult = google.picker.DocumentObject;
export type FileSelectedProps = { result: DocumentResult; file: File; data: Line[] };

type Props = {
  hasError?: boolean;
  loading?: boolean;
  fileSelected?: FileSelectedProps;
  onStartSelect?: () => void;
  onEndSelect?: () => void;
  onFileSelected?: (fileProps: FileSelectedProps) => void;
};

const GoogleSheetsPicker = styled.div<{ error?: boolean }>`
  ${typographyCss.Body3()};
  width: 100%;
  margin-block-start: 16px;
  padding: 24px 12px;
  border: 2px dashed ${({ error }) => (error ? colors.strawberry100 : cbSemanticCssVars.borderPrimary)};
  border-radius: 8px;
  text-align: center;
  outline: none;
`;

const GoogleSheetsPickerLink = styled(T.Body3)`
  cursor: pointer;
  font-weight: 500;
  color: ${colors.wolt100};
`;

const ModeToggleContainer = styled.div`
  display: flex;
  width: 100%;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
  padding: 16px 24px;
  justify-content: center;
`;

const noOp = (..._: any[]) => null;

const GoogleSheetsSelectZone = ({
  loading = false,
  hasError = false,
  onEndSelect = noOp,
  onStartSelect = noOp,
  onFileSelected = noOp,
  fileSelected,
}: Props) => {
  const [innerError, setInnerError] = useState<boolean>(false);
  const onFileBeingSelected = useCallback(async () => {
    try {
      onStartSelect();
      const fileResult = await selectGoogleSpreadsheet();
      onEndSelect();
      return fileResult;
    } catch (_) {
      setInnerError(true);
      return null;
    } finally {
      onEndSelect();
    }
  }, [onEndSelect, onStartSelect]);

  const pickGoogleSpreadsheet = useCallback(async () => {
    const result = await onFileBeingSelected();
    if (result) {
      const data = await fetchGoogleSheetsData(result.id);
      const file = makeCsvFile(data, result.name);
      onFileSelected({ result, file, data });
    }
  }, [onFileBeingSelected, onFileSelected]);

  // Translation includes a <pick /> tag
  // therefore we need to use this way of calling FormattedMessage
  return (
    <FormattedMessage
      id="menu.googleSheets.selectSpreadsheet"
      values={{
        pick: chunks => (
          <GoogleSheetsPicker error={hasError || innerError}>
            <GoogleSheetsPickerLink>
              <TextButton variant="brand" onClick={pickGoogleSpreadsheet}>
                {loading && <Spinner size="large" />}
                {!loading && (fileSelected?.file.name ? fileSelected.file.name : chunks)}
              </TextButton>
            </GoogleSheetsPickerLink>
          </GoogleSheetsPicker>
        ),
      }}
    />
  );
};

type SwitchProps = {
  csvLabel?: string;
  googleSheetsLabel?: string;
  onChange: (value: boolean) => void;
  value: boolean;
};

export const GoogleModeSwitchButton = ({
  csvLabel = 'menu.googleSheets.fromCsv',
  googleSheetsLabel = 'menu.googleSheets.fromGoogleSheets',
  value,
  onChange,
}: SwitchProps) => {
  const intl = useIntl();
  const [googleSheetsMode, setGoogleSheetsMode] = useState<boolean>(value);
  return (
    <ModeToggleContainer>
      <SegmentedControl<boolean>
        options={[
          { label: intl.formatMessage({ id: csvLabel }), value: false },
          { label: intl.formatMessage({ id: googleSheetsLabel }), value: true },
        ]}
        value={googleSheetsMode}
        onChange={value => {
          setGoogleSheetsMode(value);
          onChange(value);
        }}
      />
    </ModeToggleContainer>
  );
};

export const GoogleSelectZone = React.memo(GoogleSheetsSelectZone);
